/**
 * Service Worker registration helper.
 *
 * Sprint 1095 Phase 20 — carried over from the Phase 12 perf pass.
 *
 * Behavior:
 * - Production builds only (NODE_ENV === "production"). Dev server uses HMR
 *   over websockets and a SW caching layer causes infamously hard-to-debug
 *   stale-UI bugs. CRA's own SW template defaults to opt-in for the same
 *   reason.
 * - HTTPS only (or localhost). Browsers refuse to register a SW from
 *   plain HTTP origins.
 * - Registers at `/sw.js` (served from /public/).
 * - On `updatefound`, listens for the new worker reaching the `installed`
 *   state and posts `SKIP_WAITING` so the user gets the next deploy without
 *   a manual reload — but only after the existing tab finishes its work.
 * - Idempotent — safe to call multiple times.
 */

const SW_URL = "/sw.js";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") return;

  // Browsers only accept SW on secure contexts. `window.isSecureContext` is
  // true for HTTPS and `localhost` / `127.0.0.1`.
  if (!window.isSecureContext) return;

  // Defer registration until after `load` to keep TTI clean — registering
  // during the initial render contends with React's hydration work.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: "/" })
      .then((registration) => {
        // Listen for updates while the tab is open. When a new SW takes the
        // `installed` state with another controller active, ask it to skip
        // waiting so the user gets the next deploy on next navigation.
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              installing.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {
        // Registration failure is non-fatal — the site works without a SW.
        // No console noise: a logged error here would clutter every prod
        // page-load for users whose browsers/networks block SW registration.
      });
  });
}

export function unregisterServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((reg) => reg.unregister()))
    .catch(() => {});
}
